import { PrivateEndpointType } from 'shell-models-lib';

import { Injectable } from '@angular/core';

import { IGroupIdMetadata, IPrivateEndpointMetadata } from './private-endpoint-metadata';
import { BehaviorSubject } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PrivateEndpointRegistryService {
    private readonly _registry: IPrivateEndpointMetadata[] = [];
    public isInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly _groupIdAndResourceTypeToMetadataMap = new Map<
        PrivateEndpointType,
        Map<string, IGroupIdMetadata>
    >();

    /**
     * Maps LS type to PE metadata.
     */
    private _resourceTypeToMetadataMap: Map<string, IPrivateEndpointMetadata> = new Map();

    /**
     * Maps LS type to PE metadata.
     */
    private _linkedServiceTypeToMetadataMap: Map<string, IPrivateEndpointMetadata> = new Map();

    /**
     * Maps LS type to PE group ID metadata.
     */
    private _linkedServiceTypeToGroupIdMap: Map<string, IGroupIdMetadata> = new Map();

    public getPeMetadataByResourceType(resourceType: string): IPrivateEndpointMetadata {
        resourceType = (resourceType || '').toLowerCase();
        return this._resourceTypeToMetadataMap.get(resourceType);
    }

    public getPeMetadataByLinkedService(linkedServiceType: string): IPrivateEndpointMetadata {
        linkedServiceType = (linkedServiceType || '').toLowerCase();
        return this._linkedServiceTypeToMetadataMap.get(linkedServiceType);
    }

    public getGroupMetadataByLinkedService(linkedServiceType: string): IGroupIdMetadata {
        linkedServiceType = (linkedServiceType || '').toLowerCase();
        return this._linkedServiceTypeToGroupIdMap.get(linkedServiceType);
    }

    public getMetadataByGroupIdAndResourceType(groupId: PrivateEndpointType, resourceType: string): IGroupIdMetadata {
        resourceType = (resourceType || '').toLowerCase();
        return (
            this._groupIdAndResourceTypeToMetadataMap.get(groupId) &&
            this._groupIdAndResourceTypeToMetadataMap.get(groupId).get(resourceType)
        );
    }

    public getAllMetadata(): IPrivateEndpointMetadata[] {
        return this._registry;
    }

    public initialize(registry: IPrivateEndpointMetadata[]): void {
        if (this.isInitialized$.value) {
            return;
        }
        for (const entry of registry) {
            if (entry.disabled) {
                continue;
            }
            this._registry.push(entry);
            // associated group ID -> LS type -> group ID metadata.
        for (const groupId of Object.keys(entry.groupIdMetadata) as PrivateEndpointType[]) {
                const groupIdInfo = entry.groupIdMetadata[groupId];
                const metadataMap = this._groupIdAndResourceTypeToMetadataMap.get(groupId) || new Map();

                if (Array.isArray(entry.resourceType)) {
                    entry.resourceType.forEach((rtype) => {
                        metadataMap.set(rtype.toLowerCase(), groupIdInfo);
                    });
                } else {
                    metadataMap.set(entry.resourceType.toLowerCase(), groupIdInfo);
                }
                this._groupIdAndResourceTypeToMetadataMap.set(groupId, metadataMap);

                this._linkedServiceTypeToGroupIdMap.set(groupIdInfo.linkedServiceType.toLowerCase(), groupIdInfo); // Todo, this will be overwritten if executed multiple times
                this._linkedServiceTypeToMetadataMap.set(groupIdInfo.linkedServiceType.toLowerCase(), entry);
            }

            // group ID (endpoint type) to metadata
            if (Array.isArray(entry.resourceType)) {
                entry.resourceType.forEach((rtype) => {
                    this._resourceTypeToMetadataMap.set(rtype.toLowerCase(), entry);
                });
            } else {
                this._resourceTypeToMetadataMap.set(entry.resourceType.toLowerCase(), entry);
            }
        }
        this.isInitialized$.next(true);
    }
}
